import { resolveCognitoryApiBaseUrl } from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type GovernedLibraryDeleteResult =
  | { kind: "plan"; itemCount: number }
  | { kind: "active_serving_removal_plan"; itemCount: number }
  | { kind: "deleted" }
  | { kind: "already_deleted" }
  | {
      kind: "removal_started";
      userVisiblePhase: "deleting" | "rebuilding_library";
      workflowId?: string;
    }
  | { kind: "removal_already_pending"; userVisiblePhase: string }
  | { kind: "blocked_final_library_document" }
  | { kind: "blocked_active_serving" }
  | { kind: "unauthenticated" }
  | { kind: "forbidden" }
  | { kind: "not_found" }
  | { kind: "failure" };

export type GovernedLibraryRemovalStatusResult =
  | {
      kind: "status";
      userVisiblePhase:
        | "deleting"
        | "rebuilding_library"
        | "removed"
        | "failed";
      status: string;
    }
  | { kind: "not_found" }
  | { kind: "unauthenticated" }
  | { kind: "forbidden" }
  | { kind: "failure" };

export async function deleteGovernedLibraryDocument(input: {
  cognitoryId: string;
  documentId: string;
  mode: "dry_run" | "execute";
  confirmDocumentId?: string;
  confirmDestructiveDeletion?: true;
  bearerToken?: string;
}): Promise<GovernedLibraryDeleteResult> {
  const token =
    input.bearerToken ?? (await resolveCognitoryBearerToken()) ?? null;
  if (!token) {
    return { kind: "unauthenticated" };
  }

  const baseUrl = resolveCognitoryApiBaseUrl();
  const url = new URL(
    `${baseUrl}/api/cognitory/v2/library/documents/${encodeURIComponent(input.documentId)}/delete`,
  );
  url.searchParams.set("cognitoryId", input.cognitoryId);

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      mode: input.mode,
      ...(input.confirmDocumentId
        ? { confirmDocumentId: input.confirmDocumentId }
        : {}),
      ...(input.confirmDestructiveDeletion
        ? { confirmDestructiveDeletion: true }
        : {}),
    }),
  });

  if (response.status === 401) {
    return { kind: "unauthenticated" };
  }
  if (response.status === 403) {
    return { kind: "forbidden" };
  }
  if (response.status === 404) {
    return { kind: "not_found" };
  }

  const payload = (await response.json().catch(() => null)) as {
    decision?: string;
    plan?: { items?: unknown[] };
    pendingRemoval?: {
      userVisiblePhase?:
        | "deleting"
        | "rebuilding_library"
        | "removed"
        | "failed";
      workflowId?: string;
    };
  } | null;

  if (!payload) {
    return { kind: "failure" };
  }

  if (payload.decision === "active_serving_removal_plan") {
    return {
      kind: "active_serving_removal_plan",
      itemCount: payload.plan?.items?.length ?? 0,
    };
  }
  if (
    payload.decision === "removal_workflow_started" ||
    payload.decision === "removal_already_pending"
  ) {
    const phase = payload.pendingRemoval?.userVisiblePhase ?? "deleting";
    if (payload.decision === "removal_already_pending") {
      return {
        kind: "removal_already_pending",
        userVisiblePhase: phase,
      };
    }
    return {
      kind: "removal_started",
      userVisiblePhase:
        phase === "rebuilding_library" ? "rebuilding_library" : "deleting",
      ...(payload.pendingRemoval?.workflowId
        ? { workflowId: payload.pendingRemoval.workflowId }
        : {}),
    };
  }
  if (payload.decision === "blocked_final_library_document") {
    return { kind: "blocked_final_library_document" };
  }
  if (payload.decision === "blocked_active_serving") {
    return { kind: "blocked_active_serving" };
  }
  if (payload.decision === "deleted_verified") {
    return { kind: "deleted" };
  }
  if (payload.decision === "already_deleted") {
    return { kind: "already_deleted" };
  }
  if (payload.decision === "deletion_plan") {
    return {
      kind: "plan",
      itemCount: payload.plan?.items?.length ?? 0,
    };
  }

  if (!response.ok) {
    return { kind: "failure" };
  }

  return { kind: "failure" };
}

export async function fetchGovernedLibraryRemovalStatus(input: {
  cognitoryId: string;
  documentId: string;
  bearerToken?: string;
}): Promise<GovernedLibraryRemovalStatusResult> {
  const token =
    input.bearerToken ?? (await resolveCognitoryBearerToken()) ?? null;
  if (!token) {
    return { kind: "unauthenticated" };
  }

  const baseUrl = resolveCognitoryApiBaseUrl();
  const url = new URL(
    `${baseUrl}/api/cognitory/v2/library/documents/${encodeURIComponent(input.documentId)}/removal-status`,
  );
  url.searchParams.set("cognitoryId", input.cognitoryId);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    return { kind: "unauthenticated" };
  }
  if (response.status === 403) {
    return { kind: "forbidden" };
  }
  if (response.status === 404) {
    return { kind: "not_found" };
  }
  if (!response.ok) {
    return { kind: "failure" };
  }

  const payload = (await response.json()) as {
    decision?: string;
    pendingRemoval?: {
      status?: string;
      userVisiblePhase?:
        | "deleting"
        | "rebuilding_library"
        | "removed"
        | "failed";
    };
  };

  if (
    payload.decision === "removal_status" &&
    payload.pendingRemoval?.userVisiblePhase
  ) {
    return {
      kind: "status",
      userVisiblePhase: payload.pendingRemoval.userVisiblePhase,
      status: payload.pendingRemoval.status ?? "pending",
    };
  }

  return { kind: "failure" };
}
